import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

const PARTNERSHIP_INTERESTS = [
  "Corporate Education",
  "Industry Expertise/Speaking",
  "Research Collaboration",
  "Sponsorship",
  "Other",
];

/* ================= Hero ================= */

const PartnerApplyHero = () => (
  <section data-testid="partner-apply-hero-section">
    <Container className="pt-[41px] sm:pt-[49px] lg:pt-[90px]">
      <div className="mx-auto max-w-[62rem] text-center">
        <div className="flex items-center justify-center gap-3" aria-hidden="true">
          <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
          <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
          <span className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">
            Partnership Application
          </span>
          <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
          <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
        </div>
        <h1 className="font-serif-editorial mx-auto mt-8 max-w-4xl text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-5xl lg:text-[3.6rem]">
          Become a WFII Partner
        </h1>
        <p className="mx-auto mt-8 max-w-[64ch] text-base leading-relaxed text-muted-foreground sm:text-lg lg:max-w-none lg:text-xl">
          Complete the form below to start a partnership conversation with
          WFII.{" "}
          <br className="hidden lg:block" aria-hidden="true" />
          Our team will review your submission and follow up to discuss how we
          can work together.
        </p>
      </div>
    </Container>
  </section>
);

/* ================= Application form ================= */

const PartnerApplicationForm = () => {
  const [partnershipInterest, setPartnershipInterest] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!partnershipInterest) {
      toast.error("Please select your area of partnership interest.");
      return;
    }
    const form = e.target;
    const data = new FormData(form);
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partnership-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          organization: data.get("organization"),
          full_name: data.get("fullName"),
          job_title: data.get("jobTitle") || "",
          email: data.get("email"),
          country: data.get("country"),
          partnership_interest: partnershipInterest,
          goals: data.get("goals"),
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong while sending your application.", {
        description: "Please try again in a moment or email us directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section data-testid="partner-apply-success-section">
        <Container className="pb-[60px] lg:pb-[120px]">
          <div className="mx-auto max-w-2xl rounded-2xl bg-card/80 p-10 text-center shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] sm:p-14">
            <CheckCircle2
              className="mx-auto h-12 w-12 text-[hsl(var(--accent))]"
              strokeWidth={1.5}
              aria-hidden="true"
            />
            <h2
              className="font-serif-editorial mt-7 text-2xl font-semibold tracking-[-0.02em] text-foreground sm:text-3xl"
              data-testid="partner-apply-success-heading"
            >
              Thank you for your partnership application.
            </h2>
            <p
              className="mx-auto mt-4 max-w-[46ch] text-base leading-relaxed text-muted-foreground sm:text-lg"
              data-testid="partner-apply-success-message"
            >
              Your submission has been received. The WFII team will review your
              information and follow up with next steps.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
              <Button
                asChild
                className="h-12 bg-[hsl(var(--accent))] px-8 text-sm font-medium text-white shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.45)] transition-colors hover:bg-primary active:scale-[0.98]"
                data-testid="partner-apply-success-partners-button"
              >
                <Link to="/partners">Back to Partners</Link>
              </Button>
              <Link
                to="/"
                className="group inline-flex h-12 items-center gap-2 text-sm font-medium text-foreground transition-colors hover:text-[hsl(var(--accent))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
                data-testid="partner-apply-success-home-link"
              >
                Return Home
                <ArrowRight
                  className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                  strokeWidth={1.75}
                />
              </Link>
            </div>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <section data-testid="partner-apply-form-section">
      <Container className="pb-[60px] lg:pb-[120px]">
        <form
          onSubmit={handleSubmit}
          className="mx-auto max-w-[880px] rounded-2xl bg-card/80 p-7 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] sm:p-10 lg:p-12"
          data-testid="partner-apply-form"
        >
          <div className="grid gap-7">
            <div className="grid gap-7 sm:grid-cols-2 sm:gap-6">
              <div className="grid gap-2">
                <Label htmlFor="organization" className="text-sm font-medium text-foreground">
                  Organization Name
                </Label>
                <Input
                  id="organization"
                  name="organization"
                  required
                  autoComplete="organization"
                  placeholder="Company or organization"
                  className="h-11 bg-background"
                  data-testid="partner-apply-form-organization-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="full-name" className="text-sm font-medium text-foreground">
                  Contact Person
                </Label>
                <Input
                  id="full-name"
                  name="fullName"
                  required
                  autoComplete="name"
                  placeholder="Your full name"
                  className="h-11 bg-background"
                  data-testid="partner-apply-form-name-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="job-title" className="text-sm font-medium text-foreground">
                  Job Title/Role
                </Label>
                <Input
                  id="job-title"
                  name="jobTitle"
                  autoComplete="organization-title"
                  placeholder="Your role"
                  className="h-11 bg-background"
                  data-testid="partner-apply-form-job-title-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email" className="text-sm font-medium text-foreground">
                  Email Address
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="you@company.com"
                  className="h-11 bg-background"
                  data-testid="partner-apply-form-email-input"
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="country" className="text-sm font-medium text-foreground">
                Country
              </Label>
              <Input
                id="country"
                name="country"
                required
                autoComplete="country-name"
                placeholder="Your country"
                className="h-11 bg-background"
                data-testid="partner-apply-form-country-input"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="partnership-interest" className="text-sm font-medium text-foreground">
                Partnership Interest
              </Label>
              <Select value={partnershipInterest} onValueChange={setPartnershipInterest}>
                <SelectTrigger
                  id="partnership-interest"
                  className="h-11 bg-background"
                  data-testid="partner-apply-form-interest-trigger"
                >
                  <SelectValue placeholder="Select an area of interest" />
                </SelectTrigger>
                <SelectContent>
                  {PARTNERSHIP_INTERESTS.map((i) => (
                    <SelectItem
                      key={i}
                      value={i}
                      data-testid={`partner-apply-form-interest-option-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      {i}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="goals" className="text-sm font-medium text-foreground">
                What are you hoping to achieve through partnership with WFII?
              </Label>
              <Textarea
                id="goals"
                name="goals"
                required
                rows={6}
                placeholder="Tell us about your organization, goals, and how you'd like to work with WFII."
                className="min-h-[140px] resize-y bg-background"
                data-testid="partner-apply-form-goals-textarea"
              />
            </div>

            <p
              className="text-xs leading-relaxed text-muted-foreground"
              data-testid="partner-apply-form-consent-note"
            >
              *By submitting this form, you agree that WFII may contact you
              about partnership opportunities{" "}
              <br className="hidden lg:block" aria-hidden="true" />
              and related updates.
            </p>

            <div>
              <Button
                type="submit"
                disabled={submitting}
                className="h-12 bg-[hsl(var(--accent))] px-8 text-sm font-medium text-white shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.45)] transition-colors hover:bg-primary active:scale-[0.98] disabled:opacity-60"
                data-testid="partner-apply-form-submit-button"
              >
                {submitting ? "Submitting..." : "Submit Partnership Application"}
              </Button>
            </div>
          </div>
        </form>
      </Container>
    </section>
  );
};

/* ================= Page ================= */

export default function PartnerApplyPage() {
  return (
    <div className="relative min-h-screen">
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <main className="flex-1 space-y-[30px] lg:space-y-[60px]">
          <PartnerApplyHero />
          <PartnerApplicationForm />
        </main>
        <Footer />
        <Toaster position="bottom-right" />
      </div>
    </div>
  );
}
